<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, provide, watch, nextTick } from 'vue'
import { waitForWidgetRender } from '@/utils/dashboard/wait-for-widget-render'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from '@/composable/useI18n'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { GridLayout, GridItem } from 'vue3-grid-layout-next'
import { ChartBarIcon } from '@heroicons/vue/24/outline'

import type { IWidget, WidgetType } from '@/interfaces/components/dashboard/widget'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IWidgetFormData } from '@/interfaces/components/dashboard/widget'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { storeToRefs } from 'pinia'
import SensorResource from '@/api/resources/sensor'
import { useGroupStore } from '@/store/group'
import { DASHBOARD_TYPE_VALUES, DashboardType } from '@/interfaces/enums/dashboardType'

import WidgetModalIndicator from '@/components/Dashboard/Modals/WidgetModalIndicator.vue'
import WidgetModalGauge from '@/components/Dashboard/Modals/WidgetModalGauge.vue'
import WidgetModalBarChart from '@/components/Dashboard/Modals/WidgetModalBarChart.vue'
import WidgetModalPieChart from '@/components/Dashboard/Modals/WidgetModalPieChart.vue'
import WidgetModalTimeSeries from '@/components/Dashboard/Modals/WidgetModalTimeSeries.vue'
import WidgetModalStatusPanel from '@/components/Dashboard/Modals/WidgetModalStatusPanel.vue'
import WidgetModalOpenAlarms from '@/components/Dashboard/Modals/WidgetModalOpenAlarms.vue'
import WidgetModalTotalizers from '@/components/Dashboard/Modals/WidgetModalTotalizers.vue'
import DashboardToolbar from '@/components/Dashboard/Filters/DashboardToolbar.vue'
import CustomDashboardHeader from '@/components/Dashboard/Header/CustomDashboardHeader.vue'
import WidgetCatalog from '@/components/Dashboard/Catalog/WidgetCatalog.vue'
import CustomDashboardWidgetRenderer from '@/components/Dashboard/Widgets/Layouts/CustomDashboardWidgetRenderer.vue'
import type { TimeRangeValue } from '@/components/Globals/TimeRangeFilter'
import { useCustomDashboardWidget } from '@/composables/useCustomDashboardWidget'
import { useDashboardPolling } from '@/composables/useDashboardPolling'
import { useDashboardSensorLastReadings } from '@/composables/useDashboardSensorLastReadings'
import { clearDashboardTimeSeriesReadingsCache } from '@/composables/widgets/useDashboardTimeSeriesReadings'
import { useAlarmStore } from '@/store/alarm'
import { buildHoursRange } from '@/utils/date/date-time'

const DASHBOARD_PRESET_HOURS = {
  '1h': 1,
  '6h': 6,
  '24h': 24,
} as const

const DASHBOARD_POLLING_INTERVAL_OPTIONS = [5, 10, 15, 30, 60, 300]

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const dashboardId = computed(() => Number(route.params.id))

const store = useCompanyDashboardStore()
const alarmStore = useAlarmStore()
const { activeDashboard, widgets } = storeToRefs(store)

const dashboardPollTick = ref(0)
provide('dashboardPollTick', dashboardPollTick)

const {
  isRefreshing: isDashboardRefreshing,
  isPollingEnabled,
  pollingIntervalSeconds,
  registerRefresh,
  runRefresh,
  setPollingEnabled,
  setPollingIntervalSeconds,
  stopPolling,
} = useDashboardPolling()

const currentDashboard = computed(() => activeDashboard.value)

const dashboardTypeLabel = computed(() => {
  const type = currentDashboard.value?.dashboard_type
  if (!type || !DASHBOARD_TYPE_VALUES.includes(type as DashboardType)) return null
  return t(`custom_dashboards.types.${type as DashboardType}`)
})

const dashboardName = computed(
  () => currentDashboard.value?.name || t('custom_dashboards.view.loading'),
)

const navigateToCatalog = () => {
  router.push({
    name: 'dashboardCatalog',
    params: { companyId: route.params.companyId },
  })
}

const editMode = ref(false)
const gridInteractEnabled = ref(false)
const sidebarOpen = ref(true)
const showModal = ref(false)
const pendingType = ref<WidgetType | null>(null)
const editingWidget = ref<IWidget | null>(null)
const isSavingWidget = ref(false)
const sensors = ref<ISensor[]>([])
provide('dashboardSensors', sensors)

const sensorLastReadings = useDashboardSensorLastReadings()
provide('dashboardSensorLastReadings', sensorLastReadings)

const { getWidgetGridConstraints, getSensorForWidget } = useCustomDashboardWidget(sensors)

const periodRange = ref<TimeRangeValue | null>(null)

function onPeriodChange(value: TimeRangeValue) {
  periodRange.value = value
  store.setPeriod([value.start, value.end])
  void sensorLastReadings.refreshLastReadings(widgets.value, sensors.value, { force: true })
}

async function refreshAllDashboardData() {
  const preset = periodRange.value?.preset
  if (preset && preset !== 'custom') {
    const { start, end } = buildHoursRange(DASHBOARD_PRESET_HOURS[preset])
    periodRange.value = { start, end, preset }
    store.setPeriod([start, end], false)
  }

  dashboardPollTick.value += 1
  clearDashboardTimeSeriesReadingsCache()

  const refreshTasks: Promise<void>[] = []

  if (store.period) {
    refreshTasks.push(store.fetchAllIndicatorsData())
    refreshTasks.push(store.fetchAllTotalizersData())
    refreshTasks.push(
      sensorLastReadings.refreshLastReadings(widgets.value, sensors.value, { force: true }),
    )
  }

  if (widgets.value.some((w) => w.widget_type === 'open-alarms')) {
    refreshTasks.push(
      alarmStore.fetchOpenAlarms({ silent: true }).then(() => undefined),
    )
  }

  await Promise.all(refreshTasks)
}

const toggleEditMode = () => {
  editMode.value = !editMode.value
}

watch(editMode, (enabled) => {
  gridInteractEnabled.value = enabled
})

async function refreshGridInteract() {
  if (!editMode.value) return
  gridInteractEnabled.value = false
  await nextTick()
  gridInteractEnabled.value = true
}

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const openAddWidget = (type: WidgetType) => {
  pendingType.value = type
  editingWidget.value = null
  showModal.value = true
}

const openEditWidget = (widget: IWidget) => {
  pendingType.value = widget.widget_type
  editingWidget.value = widget
  showModal.value = true
}

const closeModal = () => {
  if (isSavingWidget.value) return
  showModal.value = false
  pendingType.value = null
  editingWidget.value = null
}

const saveWidget = async (formData: IWidgetFormData) => {
  if (!pendingType.value || !activeDashboard.value || isSavingWidget.value) return

  const widgetType = pendingType.value
  isSavingWidget.value = true

  const isTimeSeries = pendingType.value === 'time-series'
  const isStatusPanel = pendingType.value === 'status-panel'
  const isPieChart = pendingType.value === 'pie-chart'
  const isBarChart = pendingType.value === 'bar-chart'
  const isOpenAlarms = pendingType.value === 'open-alarms'
  const isTotalizers = pendingType.value === 'totalizers'
  const isGroupSource = formData.sourceType === 'group'

  const buildSourceConfig = () => {
    if (isGroupSource) {
      return {
        source_type: 'group' as const,
        group_ids: formData.groupId ? [formData.groupId] : [],
      }
    }

    return {
      source_type: 'sensor' as const,
      sensor_ids: formData.sensorId ? [formData.sensorId] : [],
    }
  }

  const pieChartConfig = isPieChart
    ? {
        ...buildSourceConfig(),
        data_fields: formData.dataFields ?? [],
        aggregation: isGroupSource ? 'last' : (formData.aggregation ?? 'mean'),
        period: periodRange.value?.preset ?? '24h',
      }
    : null

  const barChartConfig = isBarChart
    ? {
        ...buildSourceConfig(),
        data_fields: formData.dataFields ?? [],
        aggregation: isGroupSource ? 'last' : (formData.aggregation ?? 'mean'),
        period: periodRange.value?.preset ?? '24h',
      }
    : null

  const openAlarmsConfig = isOpenAlarms
    ? {
        sensor_ids: [] as string[],
        period: periodRange.value?.preset ?? '24h',
      }
    : null

  const totalizersConfig = isTotalizers
    ? {
        sensor_ids: formData.sensorId ? [formData.sensorId] : [],
        data_field: formData.dataField ?? 'demand',
        period: periodRange.value?.preset ?? '24h',
      }
    : null

  const timeSeriesSensorIds = isTimeSeries
    ? [
        ...(formData.sensorId ? [formData.sensorId] : []),
        ...(formData.secondarySensorId ? [formData.secondarySensorId] : []),
      ]
    : null

  const widgetParams: Record<string, unknown> = {
    widget_type: pendingType.value,
    title: formData.title,
    pos_x: editingWidget.value?.x ?? 0,
    pos_y: editingWidget.value?.y ?? Math.max(0, ...widgets.value.map((w) => w.y + w.h)),
    height:
      editingWidget.value?.h ??
      (pendingType.value === 'indicator' || isStatusPanel || isTotalizers
        ? 6
        : isOpenAlarms
          ? 8
          : 10),
    config: openAlarmsConfig ??
      pieChartConfig ??
      barChartConfig ??
      totalizersConfig ?? {
        ...buildSourceConfig(),
        ...(isTimeSeries
          ? { sensor_ids: timeSeriesSensorIds ?? [] }
          : {}),
        ...(isStatusPanel
          ? { data_field: 'door_open_voltage_field' }
          : formData.dataField && !isTimeSeries
            ? { data_field: formData.dataField }
            : {}),
        aggregation:
          pendingType.value === 'gauge' || isStatusPanel || isGroupSource
            ? 'last'
            : isTimeSeries
              ? 'mean'
              : formData.aggregation,
        period: periodRange.value?.preset ?? '24h',
        min: formData.min,
        max: formData.max,
        threshold: formData.threshold,
        threshold_operator: formData.thresholdOperator,
      },
  }

  if (!editingWidget.value && isTimeSeries) {
    widgetParams.width = 6
  }

  if (!editingWidget.value && isOpenAlarms) {
    widgetParams.width = 4
  }

  const isNewWidget = !editingWidget.value

  try {
    if (editingWidget.value) {
      await store.updateWidget(activeDashboard.value.id, Number(editingWidget.value.id), widgetParams)
    } else {
      await store.addWidget(activeDashboard.value.id, widgetParams)
    }

    if (
      widgetType === 'indicator' ||
      widgetType === 'gauge' ||
      widgetType === 'status-panel' ||
      widgetType === 'pie-chart' ||
      widgetType === 'bar-chart' ||
      widgetType === 'totalizers'
    ) {
      await store.fetchAllIndicatorsData()
      
      if (widgetType === 'totalizers') {
        await store.fetchAllTotalizersData()
      }
    }

    if (widgetType === 'open-alarms') {
      await alarmStore.fetchOpenAlarms()
    }

    await waitForWidgetRender()

    if (isNewWidget && editMode.value) {
      await refreshGridInteract()
    }

    isSavingWidget.value = false
    closeModal()
  } catch (error) {
    console.error('Error saving widget:', error)
    isSavingWidget.value = false
  }
}

const removeWidget = async (id: string) => {
  if (!activeDashboard.value) return

  try {
    await store.removeWidget(activeDashboard.value.id, Number(id))
  } catch (error) {
    console.error('Error removing widget:', error)
  }
}

const onLayoutUpdated = async (newLayout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => {
  if (!activeDashboard.value) return

  newLayout.forEach((item) => {
    const widget = widgets.value.find((w) => w.i === item.i)
    if (widget) {
      widget.x = item.x
      widget.y = item.y
      widget.w = item.w
      widget.h = item.h
    }
  })

  const layoutData = newLayout.map((item) => ({
    id: Number(item.i),
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  }))

  if (layoutData.length === 0) return

  try {
    await store.updateLayout(activeDashboard.value.id, layoutData)
  } catch (error) {
    console.error('Error updating layout:', error)
  }
}

onMounted(async () => {
  sensorLastReadings.startAgeTicker()

  try {
    await Promise.all([
      store.loadDashboard(dashboardId.value),
      useGroupStore().fetch(),
    ])
    sensors.value = await SensorResource.getAllSensors()

    if (periodRange.value) {
      store.setPeriod([periodRange.value.start, periodRange.value.end])
    }

    registerRefresh(refreshAllDashboardData)
    await runRefresh()
  } catch (error) {
    console.error('Error loading dashboard:', error)
  }
})

onBeforeUnmount(() => {
  stopPolling()
  sensorLastReadings.stopAgeTicker()
})
</script>

<template>
  <DefaultLayout>
    <div id="custom-dashboards-view" class="flex h-[calc(100vh-80px)] min-w-0 w-full max-w-full flex-col bg-gray-2">
      <CustomDashboardHeader
        :dashboard-name="dashboardName"
        :dashboard-type-label="dashboardTypeLabel"
        :edit-mode="editMode"
        :sidebar-open="sidebarOpen"
        @back-to-catalog="navigateToCatalog"
        @toggle-edit-mode="toggleEditMode"
        @toggle-sidebar="toggleSidebar"
      />

      <div class="flex flex-1 overflow-hidden">
        <main class="flex-1 overflow-y-auto p-6 lg:px-8">
          <div class="mb-6">
            <DashboardToolbar
              :is-refreshing="isDashboardRefreshing"
              :is-polling-enabled="isPollingEnabled"
              :polling-interval-seconds="pollingIntervalSeconds"
              :polling-interval-options="DASHBOARD_POLLING_INTERVAL_OPTIONS"
              @refresh="runRefresh"
              @change="onPeriodChange"
              @update:is-polling-enabled="setPollingEnabled"
              @update:polling-interval-seconds="setPollingIntervalSeconds"
            />
          </div>

          <div
            v-if="widgets.length === 0"
            class="mb-6 flex w-full flex-col items-center justify-center border-2 border-dashed border-stroke bg-gray-2 py-12 text-center"
          >
            <ChartBarIcon class="mb-4 h-16 w-16 text-gray-300" />
            <p class="font-satoshi text-lg font-bold text-black">
              {{ t('custom_dashboards.view.empty.title') }}
            </p>
            <p class="mt-2 text-sm text-body">
              {{ t('custom_dashboards.view.empty.description') }}
            </p>
          </div>

          <grid-layout
            v-model:layout="widgets"
            class="min-h-full"
            :col-num="12"
            :row-height="30"
            :is-draggable="true"
            :is-resizable="gridInteractEnabled"
            :vertical-compact="true"
            :margin="[12, 12]"
            :use-css-transforms="true"
            :responsive="true"
            :breakpoints="{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }"
            :cols="{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }"
            @layout-updated="onLayoutUpdated"
          >
            <grid-item
              v-for="widget in widgets"
              :key="widget.id"
              :x="widget.x"
              :y="widget.y"
              :w="widget.w"
              :h="widget.h"
              :i="widget.i"
              :min-w="getWidgetGridConstraints(widget).minW"
              :min-h="getWidgetGridConstraints(widget).minH"
              drag-allow-from=".drag-handle"
              class="flex flex-col"
            >
              <CustomDashboardWidgetRenderer
                :widget="widget"
                :edit-mode="editMode"
                :sensor="getSensorForWidget(widget)"
                @edit="openEditWidget"
                @remove="removeWidget"
              />
            </grid-item>
          </grid-layout>
        </main>

        <WidgetCatalog
          v-if="sidebarOpen"
          @add-widget="openAddWidget"
          @close="toggleSidebar"
        />
      </div>

      <Transition name="widget-modal" appear>
        <WidgetModalIndicator
          v-if="showModal && pendingType === 'indicator'"
          :sensors="sensors"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>

      <Transition name="widget-modal" appear>
        <WidgetModalGauge
          v-if="showModal && pendingType === 'gauge'"
          :sensors="sensors"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>

      <Transition name="widget-modal" appear>
        <WidgetModalBarChart
          v-if="showModal && pendingType === 'bar-chart'"
          :sensors="sensors"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>

      <Transition name="widget-modal" appear>
        <WidgetModalPieChart
          v-if="showModal && pendingType === 'pie-chart'"
          :sensors="sensors"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>

      <Transition name="widget-modal" appear>
        <WidgetModalTimeSeries
          v-if="showModal && pendingType === 'time-series'"
          :sensors="sensors"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>

      <Transition name="widget-modal" appear>
        <WidgetModalStatusPanel
          v-if="showModal && pendingType === 'status-panel'"
          :sensors="sensors"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>

      <Transition name="widget-modal" appear>
        <WidgetModalOpenAlarms
          v-if="showModal && pendingType === 'open-alarms'"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>

      <Transition name="widget-modal" appear>
        <WidgetModalTotalizers
          v-if="showModal && pendingType === 'totalizers'"
          :sensors="sensors"
          :initial-data="editingWidget || undefined"
          :loading="isSavingWidget"
          @save="saveWidget"
          @cancel="closeModal"
        />
      </Transition>
    </div>
  </DefaultLayout>
</template>

<style scoped>
/* Custom scrollbar for sidebar and canvas */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
}

:deep(.widget-modal-enter-active),
:deep(.widget-modal-leave-active) {
  transition: opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.widget-modal-enter-active .widget-modal-panel),
:deep(.widget-modal-leave-active .widget-modal-panel) {
  transition:
    opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.widget-modal-enter-from),
:deep(.widget-modal-leave-to) {
  opacity: 0;
}

:deep(.widget-modal-enter-from .widget-modal-panel),
:deep(.widget-modal-leave-to .widget-modal-panel) {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

/* Grid Layout Styles */
:deep(.vue-grid-layout) {
  background: transparent;
}

:deep(.vue-grid-item.vue-grid-placeholder) {
  background: rgba(4, 48, 76, 0.1) !important;
  border: 2px dashed #04304c !important;
  border-radius: 0 !important;
  opacity: 0.5 !important;
}

:deep(.vue-grid-item.resizing) {
  opacity: 0.9;
}

:deep(.vue-grid-item.static) {
  background: #cce;
}

:deep(.vue-grid-item .vue-resizable-handle) {
  background: none !important;
  width: 12px !important;
  height: 12px !important;
  bottom: 4px !important;
  right: 4px !important;
  border-right: 2px solid #e2e8f0 !important;
  border-bottom: 2px solid #e2e8f0 !important;
}
</style>
